import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { MessageSquare, Video, Send, Play, Users, DollarSign, Inbox } from "lucide-react";
import { toast } from "sonner";

export default function AdminCoaching() {
  const [threads, setThreads] = useState([]);
  const [subs, setSubs] = useState(null);
  const [active, setActive] = useState(null);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [filter, setFilter] = useState("open");

  const load = async () => {
    try {
      const [t, s] = await Promise.all([
        api.get(`/admin/coaching/threads${filter ? `?status=${filter}` : ""}`),
        api.get("/admin/coaching/subscriptions"),
      ]);
      setThreads(t.data.threads || []);
      setSubs(s.data);
    } catch { toast.error("Failed to load"); }
  };
  useEffect(() => { load(); }, [filter]);

  const openThread = async (id) => {
    const found = threads.find((t) => t.id === id);
    setActive(found);
    setReply("");
  };

  const sendReply = async () => {
    if (!reply.trim() || !active) return;
    setSending(true);
    try {
      const { data } = await api.post(`/admin/coaching/threads/${active.id}/reply`, { body: reply.trim() });
      setActive(data);
      setReply("");
      toast.success("Reply sent — client notified by email.");
      load();
    } catch { toast.error("Could not send"); }
    finally { setSending(false); }
  };

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="admin-coaching-page">
      <header>
        <p className="text-[10px] uppercase tracking-[0.3em] text-[#FF331F] font-bold">Ronin Coaching</p>
        <h1 className="font-display text-3xl text-white uppercase tracking-tight">Coaching Inbox</h1>
      </header>

      {subs && (
        <div className="grid sm:grid-cols-3 gap-4">
          <Stat icon={Users} label="Active subscribers" value={subs.active_count} />
          <Stat icon={DollarSign} label="Monthly recurring" value={`$${subs.mrr.toFixed(0)}`} />
          <Stat icon={Inbox} label="Open threads" value={threads.filter((t) => t.status === "open").length} />
        </div>
      )}

      <div className="flex gap-2">
        {["open", "answered", ""].map((f) => (
          <button key={f || "all"} onClick={() => { setFilter(f); setActive(null); }} data-testid={`coaching-filter-${f || "all"}`}
            className={`px-4 py-2 text-[10px] uppercase tracking-widest font-bold rounded-sm border ${filter === f ? "border-[#FF331F] bg-[#FF331F]/10 text-white" : "border-[#27272A] text-zinc-400"}`}>
            {f === "" ? "All" : f}
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        {/* Thread list */}
        <div className="lg:col-span-2 border border-[#27272A] bg-[#0F0F0F] divide-y divide-[#27272A] max-h-[600px] overflow-y-auto">
          {threads.length === 0 ? (
            <div className="p-8 text-center text-zinc-500 text-sm">No threads.</div>
          ) : threads.map((t) => {
            const Icon = t.type === "form_check" ? Video : MessageSquare;
            const last = t.messages?.[t.messages.length - 1];
            return (
              <button key={t.id} onClick={() => openThread(t.id)} data-testid={`coaching-thread-${t.id}`}
                className={`w-full text-left p-4 flex gap-3 hover:bg-[#161616] ${active?.id === t.id ? "bg-[#161616]" : ""}`}>
                <Icon className="h-4 w-4 text-[#FF331F] mt-1 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-semibold text-zinc-100 truncate">{t.user_name || t.user_email}</span>
                    {t.status === "open" && <span className="w-2 h-2 rounded-full bg-[#FF331F] shrink-0" />}
                  </div>
                  <div className="text-xs text-zinc-500 truncate">{last?.body}</div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Thread detail */}
        <div className="lg:col-span-3 border border-[#27272A] bg-[#0F0F0F] flex flex-col" style={{ minHeight: 400 }}>
          {!active ? (
            <div className="flex-1 flex items-center justify-center text-zinc-500 text-sm">Select a thread to reply.</div>
          ) : (
            <>
              <div className="border-b border-[#27272A] px-5 py-4">
                <div className="font-semibold text-white">{active.user_name || active.user_email}</div>
                <div className="text-xs text-zinc-500">{active.user_email} · {active.type === "form_check" ? "Form Check" : "Chat"}</div>
                {active.video_url && (
                  <a href={active.video_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 mt-2 text-sm text-[#FF331F] font-semibold">
                    <Play className="h-4 w-4" /> Open submitted video
                  </a>
                )}
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3 max-h-[380px]">
                {active.messages.map((m) => (
                  <div key={m.id} className={`max-w-[85%] p-3 rounded-sm ${m.sender === "coach" ? "bg-[#FF331F]/10 border border-[#FF331F] ml-auto" : "bg-[#161616] border border-[#27272A]"}`}>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-1">{m.sender === "coach" ? "You" : (active.user_name || "Client")}</div>
                    <div className="text-sm text-zinc-100 whitespace-pre-wrap">{m.body}</div>
                  </div>
                ))}
              </div>
              <div className="border-t border-[#27272A] p-3">
                <Textarea value={reply} onChange={(e) => setReply(e.target.value)} rows={3} placeholder="Write your feedback…"
                  data-testid="coaching-reply-input" className="bg-black border-[#27272A] text-white" />
                <Button onClick={sendReply} disabled={sending || !reply.trim()} data-testid="coaching-reply-send"
                  className="mt-2 w-full bg-[#FF331F] hover:bg-[#FF5242] text-white uppercase tracking-widest font-bold text-xs">
                  <Send className="h-4 w-4 mr-2" /> {sending ? "Sending…" : "Send reply"}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="border border-[#27272A] bg-[#0F0F0F] p-5">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="h-4 w-4 text-[#FF331F]" />
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">{label}</div>
      </div>
      <div className="font-display text-3xl text-white uppercase tracking-tight">{value}</div>
    </div>
  );
}
